/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { default as fragments } from '../../fragments'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Button,
  Input,
  Radio,
  SelectWithData,
  SetUserEmail,
  Notification,
  SpanWithData,
  Spinner,
  RadioBoolean,
  AddressesDisplay
} from '../'

import {
  readablePhoneNumber,
  normalizePhoneNumber
} from '../../utils'

export class IdentityAddresses extends React.Component {
  static propTypes = {
    identity: PropTypes.string.isRequired,
    heading: PropTypes.string,
    addressType: PropTypes.oneOf([
      "msisdn",
      "landline",
      "fax",
      "email",
      "physical"
    ]),
    type: PropTypes.oneOf([
      "default",
      "simple"
    ]),
    saveOnSelect: PropTypes.bool,
    inline: PropTypes.bool,
    data: PropTypes.object.isRequired,
    className: PropTypes.string
  }

  constructor(props) {
    super(props)
    this.state = {
      editAddress: false,
      addAddress: false,
      addressSelectedForEdit: false,
      addressToEdit: null,
      addressType: this.props.addressType || "Loading",
      addressValue: "Loading",
      addressDefault: "Loading",
      addressInactive: "Loading",
      addressOptedOut: "Loading",
      errorAddressDefault: false,
      errorAddressAddDuplicate: null
    }
  }

  goEditAddresses = (addressToEdit) => {
    this.setState({
      editAddress: true,
      addAddress: false,
      addressToEdit: addressToEdit.id,
      addressType: addressToEdit.addressType,
      addressValue: this.getReadableAddressValue(addressToEdit.addressValue, addressToEdit.addressType),
      addressDefault: addressToEdit.default,
      addressInactive: addressToEdit.inactive,
      addressOptedOut: addressToEdit.optedOut
    });
  }

  goAddAddress = () => {
    this.setState({
      addAddress: true,
      editAddress: false,
      addressType: this.props.addressType || '',
      addressValue: "",
      addressDefault: this.props.type === "simple" ? true : false,
      addressInactive: false,
      addressOptedOut: false
    });
  }

  getReadableAddressValue = (addressValue, addressType) => {
    if (['MSISDN', 'LANDLINE', 'FAX'].indexOf(addressType) > -1) {
      return readablePhoneNumber(addressValue)
    } else {
      return addressValue
    }
  }

  getNormalizedAddressValue = (addressValue) => {
    if (['MSISDN', 'LANDLINE', 'FAX'].indexOf(this.state.addressType.toUpperCase()) > -1) {
      return normalizePhoneNumber(addressValue)
    } else {
      return addressValue
    }
  }

  goReadOnly = () => {
    this.setState({
      editAddress: false,
      addAddress: false,
      addressToEdit: null,
      saving: false
    })
  }

  canSave = (address, index) => {
    return address[index].node.addressType !== this.state.addressType
        || address[index].node.addressValue !== this.state.addressValue
        || address[index].node.default !== this.state.addressDefault
        || address[index].node.inactive !== this.state.addressInactive
        || address[index].node.optedOut !== this.state.addressOptedOut
  }

  saveNewAddress = (addressId, addressType, addressValue) => {
    let input = {
      addressType: addressType.toLowerCase(),
      addressValue: this.getNormalizedAddressValue(addressValue),
      default: true,
      inactive: false,
      optedOut: false,
      identity: this.props.data.identity.id
    }

    if (addressId !== null) {  // addressId being present is edit instead of addition
      input.id = addressId
    }

    this.props.updateIdentityAddressDetails({ variables: { input }})
    .then(({ data }) => {
      this.props.data.refetch()
      .then(this.goReadOnly)
    })
    .catch((error) => {
      console.log('there was an error saving new address', error)
      this.handleErrorOnAddressAddition(error)
    })
  }

  handleErrorOnAddressAddition = (error) => {
    if (error.hasOwnProperty('graphQLErrors') && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message.includes('duplicated')) {
      // now get error message nested in error object
      let graphQlErrorMessage = error.graphQLErrors[0].message
      let errorAddressAddDuplicate = graphQlErrorMessage.substring(
        graphQlErrorMessage.indexOf('"') + 1,
        graphQlErrorMessage.lastIndexOf('"')
      )
      this.setState({ errorAddressAddDuplicate })
    }
  }

  handleChange = (addressId, addressType, addressValue) => {
    if (addressId !== undefined) {
      // in case of simple/saveOnSelect being specified as prop;
      // saving of address happens on radio button select &
      // adding first resets current default

      // set current save default address false
      let currentDefaultAddress = this.props.data.identity.addresses.edges
      .filter((address) => (
        address.node.addressType.toLowerCase() === this.props.addressType &&
        address.node.default
      ))

      if (currentDefaultAddress.length > 0) {
        this.props.updateIdentityAddressDetails({
          variables: {
            input: {
              id: currentDefaultAddress[0].node.id,
              default: false
            }
          }
        })
        .then(() => {
          console.log('Current default address successfully set to false')
          // save newly selected address as default
          this.saveNewAddress(addressId, addressType, addressValue)
        })
        .catch((error) => console.log('Current default address setting to false failed', error))
      } else {
        this.saveNewAddress(addressId, addressType, addressValue)
      }
    } else {
      if (this.state.editAddress) {
        // Edit
        let identityAddressEdit = this.props.data.identity.addresses.edges
        let index = identityAddressEdit.findIndex(
          (element, index, array) => {
            return element.node.id === this.state.addressToEdit
          }
        )
        if (this.canSave(identityAddressEdit, index)) {
          let addressInfo = {
            id: this.state.addressToEdit
          }

          if (identityAddressEdit[index].node.addressType !== this.state.addressType)
            addressInfo.addressType = this.state.addressType.toLowerCase()

          if (identityAddressEdit[index].node.addressValue !== this.state.addressValue)
            addressInfo.addressValue = this.getNormalizedAddressValue(this.state.addressValue)

          if (identityAddressEdit[index].node.default !== this.state.addressDefault)
            addressInfo.default = this.state.addressDefault

          if (identityAddressEdit[index].node.inactive !== this.state.addressInactive)
            addressInfo.inactive = this.state.addressInactive

          if (identityAddressEdit[index].node.optedOut !== this.state.addressOptedOut)
            addressInfo.optedOut = this.state.addressOptedOut

          this.props.updateIdentityAddressDetails({variables: {input: addressInfo}})
          .then(({ data }) => {
            this.props.data.refetch()
            .then(this.goReadOnly)
          })
          .catch((error) => console.log('there was an error editing address', error))
        } else {
          this.goReadOnly()
        }
      } else {
        // Add
        let addressInfo = {
          addressType: this.state.addressType.toLowerCase(),
          addressValue: this.getNormalizedAddressValue(this.state.addressValue),
          default: this.state.addressDefault,
          inactive: this.state.addressInactive,
          optedOut: this.state.addressOptedOut
        }

        addressInfo.identity = this.props.data.identity.id
        this.props.updateIdentityAddressDetails({variables: {input: addressInfo}})
          .then(({ data }) => {
            this.props.data.refetch()
            .then(this.goReadOnly)
          })
          .catch((error) => {
            console.log('there was an error adding address', error)
            this.handleErrorOnAddressAddition(error)
          })
      }
    }
  }

  getValues = (address) => (
    this.props.saveOnSelect
    ? [
        this.getReadableAddressValue(address.addressValue, address.addressType),
        <Radio
          id={`${address.addressValue}_default`}
          onClick={() => this.handleChange(address.id, address.addressType, address.addressValue)}
          checked={address.default}
        />
      ]
    : [
        this.getReadableAddressValue(address.addressValue, address.addressType),
        <Radio
          id={`${address.addressValue}_default`}
          onChange={() => this.setState({ default: true })}
          disabled={this.props.choiceConfirmed}
          checked={address.default}
        />,
        <span>
          <Button
            onClick={(e) => this.goEditAddresses(address)}>
            Edit
          </Button>
          {this.props.loginButton && address.addressType === "EMAIL"
            ? <SetUserEmail
                identity={this.props.identityUUID}
                email={address.addressValue}
              />
            : <span></span>
          }
        </span>
      ]
  )

  getAddressesRowsForSimpleView = (addresses) => {
    let rows = []
    let template = [
        <Input
          id="value"
          value={this.state.addressValue}
          placeholder=""
          onChange={(e) => this.setState({ addressValue: e.target.value })}
        />,
        <RadioBoolean
          name="addressDefault"
          value={this.state.addressDefault}
          onChange={(value) => this.setState({ addressDefault: value === "yes" })}
        />,
        <Button
          onClick={this.handleChange}
          disabled={this.state.errorAddressDefault}>
          Save
        </Button>
      ]

    if (this.props.saveOnSelect) {
      template.pop()
    }

    addresses.forEach((address,i) => (
      this.state.editAddress && this.state.addressToEdit === address.node.id
        ? rows.push(template)
        : rows.push(this.getValues(address.node))
    ))

    if(this.state.addAddress) {
      // blank for addition
      rows.push(template)
    }

    return rows
  }

  getAddressesRows = (addresses) => {
    let rows = []

    let template = [
        <SelectWithData
          id="type"
          forField="AddressAddressType"
          value={this.state.addressType}
          onChange={(e) => this.setState({addressType: e.target.value})}
        />,
        <Input
          id="value"
          value={this.state.addressValue}
          placeholder=""
          onChange={(e) => this.setState({addressValue: e.target.value})}
        />,
        <RadioBoolean
          name="addressDefault"
          value={this.state.addressDefault}
          onChange={(value) => this.setAddressDefault(value)}
          hasError={this.state.errorAddressDefault}
          errorLabel="Only one default address per type is allowed"
        />,
        <RadioBoolean
          name="addressInactive"
          value={this.state.addressInactive}
          onChange={(value) => this.setState({addressInactive: value === "yes"})}
        />,
        <RadioBoolean
          name="addressOptedOut"
          value={this.state.addressOptedOut}
          onChange={(value) => this.setState({addressOptedOut: value === "yes"})}
        />,
        <Button
          onClick={this.handleChange}
          disabled={this.state.errorAddressDefault}>
          Save
        </Button>
      ]

    addresses.map((record,i) => (
      this.state.editAddress && this.state.addressToEdit === record.node.id
        ? rows.push(template)
        : rows.push([
            <SpanWithData forField="AddressAddressType" searchKey={record.node.addressType} />,
            this.getReadableAddressValue(record.node.addressValue, record.node.addressType),
            record.node.default ? "Yes" : "No",
            record.node.inactive ? "Yes" : "No",
            record.node.optedOut ? "Yes" : "No",
            <span>
              <Button
                onClick={(e) => this.goEditAddresses(record.node)}>
                Edit
              </Button>
              {this.props.loginButton && record.node.addressType === "EMAIL"
                ? <SetUserEmail
                    identity={this.props.identityUUID}
                    email={record.node.addressValue}
                  />
                : <span></span>
              }
            </span>
          ])
    ))

    if(this.state.addAddress) {
      // blank for addition
      rows.push(template)
    }

    return rows
  }

  // get address set as default by current selected addressType
  getDefaultAddressID = (addresses) => (
    addresses.find((address) => (
      (address.node.addressType === this.state.addressType.toUpperCase()) && address.node.default
    ))
  )

  setAddressDefault = (value) => {
    let addressCurrentlySetAsDefault = this.getDefaultAddressID(this.props.data.identity.addresses.edges)
    let defaultAddressID = addressCurrentlySetAsDefault ? addressCurrentlySetAsDefault.node.id : null
    if (value === "yes") {
      if (defaultAddressID === this.state.addressToEdit) {
        // set address as default only if no address is already set to default
        this.setState({
          addressDefault: true,
          errorAddressDefault: false
        })
      } else {
        if (defaultAddressID === null ||
          (defaultAddressID === null && this.state.addAddress)) {
          this.setState({
            addressDefault: true
          })
        } else {
          this.setState({ errorAddressDefault: true })
        }
      }
    } else {
      if (this.state.errorAddressDefault) {
        this.setState({
          addressDefault: false,
          errorAddressDefault: false,
          editAddress: false
        })
      } else {
        this.setState({ addressDefault: false })
      }
    }
  }

  getAddressTypeDescription = (addressType) => (
    {
      "email": "Email",
      "msisdn": "Cellphone",
      "landline": "Landline",
      "fax": "Fax",
      "physical": "Physical"
    }[addressType]
  )

  render() {
    if (this.props.data.loading) {
      return <Spinner type="brand" />
    }

    if (this.props.data.error) {
      return <h1>Oh dear! We have an issue</h1>
    }

    let addresses = this.props.addressType
      ? this.props.data.identity.addresses.edges.filter((address) => (
          address.node.addressType.toLowerCase() === this.props.addressType
        ))
      : this.props.data.identity.addresses.edges

    let rows = this.props.type === "simple"
      ? this.getAddressesRowsForSimpleView(addresses)
      : this.getAddressesRows(addresses)

    return (
      <React.Fragment>
        {this.state.errorAddressAddDuplicate &&
          <Notification
            type='warning'
            message={this.state.errorAddressAddDuplicate}
            onCloseClick={() => this.setState({ errorAddressAddDuplicate: null })}
            className='slds-m-bottom_small'
          />
        }
        <AddressesDisplay
          title={this.props.heading || (this.props.addressType ? `Addresses: ${this.getAddressTypeDescription(this.props.addressType)}`: "Addresses")}
          simple={this.props.type === "simple"}
          noHeader={this.props.inline}
          noActionColumn={this.props.saveOnSelect}
          rows={rows}
          saveAdd={() => {
            this.setState({ saving: true })
            this.props.type === "simple"
            ? this.handleChange(null, this.props.addressType, this.state.addressValue)
            : this.handleChange()
          }}
          saveDisabled={this.state.saving}
          addMode={this.state.addAddress}
          goBack={this.props.goBack}
          goReadOnly={this.goReadOnly}
          goEdit={this.goEditAddresses}
          goAdd={this.goAddAddress}
          className={this.props.className}
        />
      </React.Fragment>
    )
  }
}

const getIdentityAddresses = gql`query($id: ID!) {
    identity(id: $id) {
      id
      addresses {
        edges {
          node {
            ...Address
          }
        }
      }
    }
  }
  ${fragments.address}
`

const updateIdentityAddressDetails = gql`
  mutation updateIdentityAddressDetails($input: AddressMutationInput!) {
    addressMutate(input: $input) {
      address {
        ...Address
      }
    }
  }
  ${fragments.address}
`

export const withData =
  graphql(getIdentityAddresses, {
    options: (ownProps) => ({
      variables: {
        id: ownProps.identity
      }
    })
  })

export const withUpdate =
  graphql(updateIdentityAddressDetails, {
    name: 'updateIdentityAddressDetails'
  })

export default compose(
  withData,
  withUpdate
)(IdentityAddresses)



// WEBPACK FOOTER //
// ./src/components/identity-addresses/index.js